import { JSX, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaKey } from 'react-icons/fa6';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { useLocation } from 'react-router-dom';
import { Button } from '../components/button';
import { Error } from '../components/error';
import { Form } from '../components/form';
import { useAuthContext } from '../contexts/auth.context';
import { useFirebase } from '../hooks/firebase.hook';
import { useLayout } from '../hooks/layout.hook';
import { useNavigation } from '../hooks/navigation.hook';

interface FormData {
  password: string;
}

export function AuthResetScreen(): JSX.Element {
  const location = useLocation();
  const { t } = useTranslation();
  const { resetPassword } = useAuthContext();
  const { translateError } = useFirebase();
  const { navigate } = useNavigation();

  const queryParams = new URLSearchParams(location.search);
  const oobCode = queryParams.get('oobCode');

  const [error, setError] = useState<string>();
  const [showPassword, setShowPassword] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  useEffect(() => {
    if (!oobCode) navigate('/');
  }, [oobCode]);

  function handleSubmit({ password }: FormData) {
    if (!oobCode) return;

    setError(undefined);
    setIsResetting(true);

    resetPassword(oobCode, password)
      .then(() => navigate('/auth', { state: { isSignUp: false } }))
      .catch((error) => setError(translateError(error.message)))
      .finally(() => setIsResetting(false));
  }

  useLayout(
    () => ({
      hideFooter: true,
      title: t('Reset password'),
      backButton: true,
      onBack: () => navigate('/'),
    }),
    [t],
  );

  return (
    <>
      <p className="text-center text-base-content/70 mb-2">
        {t('Please select a new password to protect your Virtus account.')}{' '}
      </p>

      <Form onSubmit={handleSubmit}>
        <div className="flex flex-col gap-4">
          <div>
            <label className="input input-lg validator w-full relative">
              <FaKey className="text-base-content/70" />
              <input type={showPassword ? 'text' : 'password'} name="password" placeholder="*****" required />

              <div
                className="top-0 right-0 absolute h-full flex items-center cursor-pointer p-3"
                onClick={() => setShowPassword((prev) => !prev)}
              >
                {showPassword ? <MdVisibility size={'1.5rem'} /> : <MdVisibilityOff size={'1.5rem'} />}
              </div>
            </label>
            <div className="validator-hint hidden">{t('Required')}</div>
          </div>

          {error && <Error msg={error} />}

          <Button className="btn btn-primary btn-lg" type="submit" loading={isResetting}>
            {t('Reset')}
          </Button>
        </div>
      </Form>
    </>
  );
}
